import { Button } from '@/components/Button';
import { MaterialIcon } from '@/components/MaterialIcon';
import { useState, useEffect } from 'react';
import useFetchIam from '@/features/agent/api/useFetchIam';
import type AgentApiOrganizationTutorialsIndex from '@/types/serializers/AgentApiOrganizationTutorialsIndex';
import type AgentApiOrganizationTutorialsRecord from '@/types/serializers/AgentApiOrganizationTutorialsRecord';
import type AgentApiOrganizationTutorialsItem from '@/types/serializers/AgentApiOrganizationTutorialsItem';
import usePostOrganizationTutorialComplete from '@/features/agent/api/usePostOrganizationTutorialComplete';
import { showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import CompaniesAddModal from '@/features/agent/companies/components/CompaniesAddModal';
import CandidatesAddModal from '@/features/agent/candidates/components/CandidatesAddModal';

// イラスト・アイコン画像パス
const ILLUST_MANAGE = '/agent/onboarding/management.png';
const ILLUST_SUGGEST = '/agent/onboarding/suggestion.png';
const ILLUST_ENTRY = '/agent/onboarding/making-entryform.png';

// ItemType型定義
type ItemType = AgentApiOrganizationTutorialsItem['item_type'];

// item_typeに応じたボタン文言のマッピング
const getButtonConfig = (itemType: ItemType) => {
  const buttonConfigs: Record<ItemType, { label: string; width: string }> = {
    created_job_posts: { label: '求人票を作成', width: 'w-[128px]' },
    created_candidate_profiles: { label: '求職者を登録', width: 'w-[128px]' },
    created_companies: { label: '企業を登録', width: 'w-[128px]' },
    suggested_job_posts: { label: '求職者を選ぶ', width: 'w-[128px]' },
    defined_candidate_custom_fields: { label: 'データを定義', width: 'w-[128px]' },
    created_entry_forms: { label: 'エントリーフォームを作成', width: 'w-[210px]' },
  };

  return buttonConfigs[itemType];
};

// item_typeの組み合わせでチュートリアルの種類を判定
const getTutorialType = (items: AgentApiOrganizationTutorialsItem[]) => {
  const itemTypes = items.map((item) => item.item_type);

  // 優先順位に基づいてチュートリアルタイプを決定
  const priorityItemType = itemTypes.find((type) =>
    ['created_companies', 'suggested_job_posts', 'created_entry_forms'].includes(type)
  );

  switch (priorityItemType) {
    case 'created_companies':
      return 'data_management' as const;
    case 'suggested_job_posts':
      return 'suggest_job_post' as const;
    case 'created_entry_forms':
      return 'entry_form' as const;
    default:
      return 'data_management' as const; // デフォルト
  }
};

// TutorialType型定義
type TutorialType = ReturnType<typeof getTutorialType>;

// tutorial_typeに応じたイラストを返す
const getTutorialIllust = (tutorialType: TutorialType) => {
  const illustMap: Record<TutorialType, string> = {
    data_management: ILLUST_MANAGE,
    suggest_job_post: ILLUST_SUGGEST,
    entry_form: ILLUST_ENTRY,
  };
  return illustMap[tutorialType];
};

// item_typeに応じたアイコンを返す
const getStepIcon = (itemType: ItemType) => {
  const iconMap: Record<ItemType, string> = {
    created_job_posts: 'note_stack_add',
    created_candidate_profiles: 'groups',
    created_companies: 'groups',
    suggested_job_posts: 'mail',
    defined_candidate_custom_fields: 'settings',
    created_entry_forms: 'content_paste',
  };
  return iconMap[itemType];
};

function StepButton({
  label,
  variant,
  width,
  onClick,
  disabled = false,
}: {
  label: string;
  variant: 'primary' | 'secondary';
  width: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      type="button"
      color={variant === 'primary' ? 'primary' : 'secondary'}
      size="md"
      className={`${width} ${disabled ? 'cursor-not-allowed' : ''}`}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      <span>{label}</span>
      <MaterialIcon name="keyboard_arrow_right" className="text-sm" />
    </Button>
  );
}

function StepCard({
  record,
  onSkip,
}: {
  record: AgentApiOrganizationTutorialsRecord;
  onSkip: () => void;
}) {
  const { data: iam } = useFetchIam();
  const [isCompaniesModalOpen, setIsCompaniesModalOpen] = useState(false);
  const [isCandidatesModalOpen, setIsCandidatesModalOpen] = useState(false);

  // titleから実際のタイトルを抽出（例: "{title: \"データの管理をしたい\"}" → "データの管理をしたい"）
  const extractTitle = (titleStr: string) => {
    const match = titleStr.match(/title: "([^"]+)"/);
    return match ? match[1] : titleStr;
  };

  // itemsの処理 - 文字列の場合はパース、オブジェクトの場合はそのまま使用
  const parseItems = (
    items: AgentApiOrganizationTutorialsItem[] | string[]
  ): AgentApiOrganizationTutorialsItem[] => {
    if (Array.isArray(items) && items.length > 0) {
      if (typeof items[0] === 'string') {
        // 文字列の場合はパース
        return (items as string[]).map((item) => JSON.parse(item));
      } else {
        // 既にオブジェクトの場合はそのまま
        return items as AgentApiOrganizationTutorialsItem[];
      }
    }
    return [];
  };

  // ボタンクリック時の処理
  const handleButtonClick = (itemType: ItemType) => {
    const organizationId = iam?.current_organization.id;

    if (!organizationId) return;

    switch (itemType) {
      case 'created_job_posts':
        // 求人票を作成ボタン - CompaniesAddModalを表示
        setIsCompaniesModalOpen(true);
        break;
      case 'created_candidate_profiles':
        // 求職者を登録ボタン - CandidatesAddModalを表示
        setIsCandidatesModalOpen(true);
        break;
      case 'created_companies':
        // 企業を登録ボタン - CompaniesAddModalを表示
        setIsCompaniesModalOpen(true);
        break;
      case 'suggested_job_posts':
        // 求人を提案ボタン - 求職者一覧ページに遷移
        window.location.href = '/agent/candidates';
        break;
      case 'defined_candidate_custom_fields':
        // データを定義ボタン
        window.location.href = `/agent/candidates/custom`;
        break;
      case 'created_entry_forms':
        // エントリーフォームを作成ボタン（機能削除）
        break;
      default:
        break;
    }
  };

  const title = extractTitle(record.title);
  const tutorialItems = parseItems(record.items as AgentApiOrganizationTutorialsItem[] | string[]);
  const tutorialType = getTutorialType(tutorialItems);
  const config = { illust: getTutorialIllust(tutorialType), getStepIcon };

  // ハイライト対象のステップを決定（STEP順序ベース）
  const getHighlightStep = (items: AgentApiOrganizationTutorialsItem[]) => {
    // step順でソート
    const sortedItems = [...items].sort((a, b) => a.step - b.step);

    // 最初の未完了項目を探す
    const firstIncompleteItem = sortedItems.find((item) => !item.is_completed);

    // 未完了項目がある場合はそのstep、全て完了している場合は最後のstep
    return firstIncompleteItem
      ? firstIncompleteItem.step
      : sortedItems[sortedItems.length - 1]?.step;
  };

  const highlightStep = getHighlightStep(tutorialItems);

  return (
    <>
      <div className="mb-6 flex w-[1020px] overflow-hidden rounded-sm border border-solid border-border bg-white text-center shadow-xs">
        {/* 左：イラスト＋タイトル */}
        <div className="flex w-[348px] flex-col items-center justify-center bg-[#EDF8FF] px-6 py-[28px]">
          <div className="mb-4 bg-white px-2 py-1 text-center text-xl leading-[140%] font-bold text-primary">
            {title}
          </div>
          <img src={config.illust} alt="illust" className="h-[186px] w-[230px] object-contain" />
        </div>
        {/* 右：ステップ群 */}
        <div className="relative flex-1 px-8 py-[28px]">
          {/* 閉じるボタン */}
          <button
            type="button"
            onClick={onSkip}
            className="absolute top-4 right-4 size-[42px] rounded border border-solid border-border p-[10px] hover:bg-gray-100"
          >
            <MaterialIcon name="close" className="text-xl" />
          </button>
          <div className="flex items-center gap-xxl">
            {tutorialItems.map((item, _) => {
              const isHighlighted = item.step === highlightStep;
              const isCompleted = item.is_completed;

              return (
                <div key={item.step} className="flex w-[160px] flex-col items-center">
                  {/* STEPバッジ */}
                  <div
                    className={`mb-2 rounded-full px-4 py-1 text-xs leading-[150%] font-bold text-white ${
                      isCompleted || isHighlighted ? 'bg-primary' : 'bg-[#6D6D6D]'
                    }`}
                  >
                    STEP {item.step}
                  </div>
                  {/* ラベル */}
                  <div className={`mb-2 text-center text-sm leading-[170%] text-[#262626]`}>
                    {(() => {
                      const text = item.title;
                      const index = text.indexOf('を');
                      if (index !== -1) {
                        // 最初の「を」で分割
                        const firstPart = text.substring(0, index + 1); // 「を」を含む前半
                        const secondPart = text.substring(index + 1); // 「を」以降の後半
                        return (
                          <>
                            <div key="first">{firstPart}</div>
                            <div key="second">{secondPart}</div>
                          </>
                        );
                      } else {
                        // 「を」が含まれていない場合はそのまま表示
                        return <div key="single">{text}</div>;
                      }
                    })()}
                  </div>
                  {/* アイコン */}
                  <div
                    className={`mb-4 flex size-[84px] items-center justify-center rounded-full ${
                      isCompleted || isHighlighted ? 'bg-[#EDF8FF]' : 'bg-[#F6F6F6]'
                    }`}
                  >
                    <MaterialIcon
                      name={config.getStepIcon(item.item_type)}
                      className={`text-[40px] opacity-40 ${
                        isCompleted || isHighlighted ? 'text-primary' : ''
                      }`}
                    />
                  </div>
                  {/* ボタン */}
                  <StepButton
                    label={isCompleted ? '完了' : getButtonConfig(item.item_type).label}
                    variant={isCompleted || isHighlighted ? 'primary' : 'secondary'}
                    width={getButtonConfig(item.item_type).width}
                    onClick={() => handleButtonClick(item.item_type)}
                    disabled={isCompleted}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* CompaniesAddModal */}
      <CompaniesAddModal
        isOpen={isCompaniesModalOpen}
        onClose={() => setIsCompaniesModalOpen(false)}
      />

      {/* CandidatesAddModal */}
      <CandidatesAddModal
        isOpen={isCandidatesModalOpen}
        onClose={() => setIsCandidatesModalOpen(false)}
      />
    </>
  );
}

export default function Onboarding({
  onComplete,
  tutorials,
  refetchTutorials,
}: {
  onComplete?: () => void;
  tutorials: AgentApiOrganizationTutorialsIndex;
  refetchTutorials?: () => Promise<unknown>;
}) {
  const [skippedCards, setSkippedCards] = useState<Set<number>>(new Set());
  const { mutateAsync: postOrganizationTutorialComplete } = usePostOrganizationTutorialComplete();

  const handleSkipCard = async (recordId: number) => {
    try {
      await postOrganizationTutorialComplete(recordId);
    } catch (error) {
      showErrorToast('スキップに失敗しました');
    }
    const newSkippedCards = new Set(skippedCards);
    newSkippedCards.add(recordId);
    setSkippedCards(newSkippedCards);
  };

  // 全てのカードがスキップされた場合の処理
  useEffect(() => {
    if (tutorials && skippedCards.size === tutorials.records.length) {
      const completeSetup = async () => {
        // React Queryの状態を明示的に更新
        if (refetchTutorials) {
          await refetchTutorials();
        }

        if (onComplete) {
          onComplete();
        } else {
          window.location.reload();
        }
      };

      completeSetup();
    }
  }, [skippedCards, tutorials, onComplete, refetchTutorials]);

  // スキップされていないレコードをフィルタリング
  const visibleRecords = tutorials?.records.filter((record) => !skippedCards.has(record.id)) || [];

  // 全てスキップされている場合
  if (visibleRecords.length === 0) {
    return null;
  }

  return (
    <div
      className="flex min-h-screen flex-col items-center py-8"
      style={{ backgroundColor: '#F6F6F6' }}
    >
      <h2 className="mb-6 text-center text-2xl font-bold text-[#262626]">
        まずは、次の設定からはじめてみましょう！
      </h2>
      {visibleRecords.map((record) => (
        <StepCard key={record.id} record={record} onSkip={() => handleSkipCard(record.id)} />
      ))}
    </div>
  );
}
